import React, { useEffect, useContext, useRef, useState } from "react";
import { AppSettings } from "../../config/app-settings.js";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { login } from "../../app/auth/authSlice.js";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import "./login.css";

const EMPTY_ERRORS = { userName: "", password: "" };

function Login() {
  const context = useContext(AppSettings);
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [keepMeLogin, setKeepMeLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [capsLockOn, setCapsLockOn] = useState(false);
  const [showResetHelp, setShowResetHelp] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const userNameRef = useRef(null);
  const passwordRef = useRef(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const savedKeepMeLogin = localStorage.getItem("keepMeLogin");
    if (savedKeepMeLogin !== null) {
      setKeepMeLogin(JSON.parse(savedKeepMeLogin));
    }
  }, []);

  useEffect(() => {
    context.handleSetAppSidebarNone(true);
    context.handleSetAppHeaderNone(true);
    context.handleSetAppContentClass("p-0");

    return function cleanUp() {
      context.handleSetAppSidebarNone(false);
      context.handleSetAppHeaderNone(false);
      context.handleSetAppContentClass("");
    };
  }, [context]);

  const validateForm = () => {
    const nextErrors = { ...EMPTY_ERRORS };

    if (!userName.trim()) {
      nextErrors.userName = "Username is required";
    }
    if (!password.trim()) {
      nextErrors.password = "Password is required";
    }

    setErrors(nextErrors);

    if (nextErrors.userName) {
      userNameRef.current?.focus();
      return false;
    }
    if (nextErrors.password) {
      passwordRef.current?.focus();
      return false;
    }

    return true;
  };

  const handleCapsLock = (event) => {
    if (typeof event.getModifierState === "function") {
      setCapsLockOn(event.getModifierState("CapsLock"));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await axiosInstance.post(APIs.LOGIN, {
        userName,
        password,
        keepMeLogin,
        clientType: "mobile",
      });

      if (response.status === 200 && response.data.status) {
        const userData = {
          token: response.data.data.token,
          userData: response.data.data.userData,
          refreshToken: response.data.data.refreshToken,
          keepMeLogin,
        };

        if (response.data.data.refreshToken) {
          localStorage.setItem("refreshToken", response.data.data.refreshToken);
        }

        localStorage.setItem("keepMeLogin", JSON.stringify(keepMeLogin));

        dispatch(login(userData));
        showSuccessToast("Logged in Successfully.");
        navigate("/dashboard");
      } else {
        showErrorToast(response.data.message || "Login failed");
      }
    } catch (err) {
      showErrorToast(err?.response?.data?.message ?? "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const renderLogo = () => {
    if (logoFailed) {
      return (
        <span className="sa-login__wordmark">
          Serve<span>IQ</span>
        </span>
      );
    }

    return (
      <img
        src="/assets/img/login-logo.png"
        alt="ServeIQ"
        className="sa-login__logo"
        onError={() => setLogoFailed(true)}
      />
    );
  };

  return (
    <div className="sa-login">
      <div className="sa-login__card">
        <div className="sa-login__header">
          {renderLogo()}
          <span className="sa-login__badge">
            <i className="fas fa-shield-halved" aria-hidden></i>
            Super Admin
          </span>
          <h1 className="sa-login__title">Sign in</h1>
          <p className="sa-login__subtitle">
            Access the Super Admin console with your credentials.
          </p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="sa-login__field">
            <label className="sa-login__label" htmlFor="userName">
              Username
            </label>
            <div
              className={`sa-login__input-wrap${errors.userName ? " is-invalid" : ""}`}
            >
              <i className="fas fa-user sa-login__input-icon" aria-hidden></i>
              <input
                id="userName"
                ref={userNameRef}
                type="text"
                className="sa-login__input"
                value={userName}
                placeholder="Enter your username"
                autoComplete="username"
                disabled={loading}
                aria-invalid={Boolean(errors.userName)}
                aria-describedby={errors.userName ? "userName-error" : undefined}
                onChange={(e) => {
                  setUserName(e.target.value);
                  if (errors.userName) setErrors((prev) => ({ ...prev, userName: "" }));
                }}
              />
            </div>
            {errors.userName && (
              <p className="sa-login__error" id="userName-error">
                <i className="fas fa-circle-exclamation" aria-hidden></i>
                {errors.userName}
              </p>
            )}
          </div>

          <div className="sa-login__field">
            <label className="sa-login__label" htmlFor="password">
              Password
            </label>
            <div
              className={`sa-login__input-wrap${errors.password ? " is-invalid" : ""}`}
            >
              <i className="fas fa-lock sa-login__input-icon" aria-hidden></i>
              <input
                id="password"
                ref={passwordRef}
                type={showPassword ? "text" : "password"}
                className="sa-login__input sa-login__input--with-action"
                value={password}
                placeholder="Enter your password"
                autoComplete="current-password"
                disabled={loading}
                aria-invalid={Boolean(errors.password)}
                aria-describedby={errors.password ? "password-error" : undefined}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors((prev) => ({ ...prev, password: "" }));
                }}
                onKeyUp={handleCapsLock}
                onKeyDown={handleCapsLock}
                onBlur={() => setCapsLockOn(false)}
              />
              <button
                type="button"
                className="sa-login__reveal"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i
                  className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                  aria-hidden
                ></i>
              </button>
            </div>
            {errors.password && (
              <p className="sa-login__error" id="password-error">
                <i className="fas fa-circle-exclamation" aria-hidden></i>
                {errors.password}
              </p>
            )}
            {capsLockOn && !errors.password && (
              <p className="sa-login__hint sa-login__hint--warn" role="status">
                <i className="fas fa-arrow-up" aria-hidden></i>
                Caps Lock is on
              </p>
            )}
          </div>

          <div className="sa-login__row">
            <label className="sa-login__check" htmlFor="rememberMe">
              <input
                id="rememberMe"
                type="checkbox"
                checked={keepMeLogin}
                disabled={loading}
                onChange={(e) => setKeepMeLogin(e.target.checked)}
              />
              <span>Keep me signed in</span>
            </label>
            <button
              type="button"
              className="sa-login__link"
              onClick={() => setShowResetHelp((prev) => !prev)}
              aria-expanded={showResetHelp}
            >
              Forgot password?
            </button>
          </div>

          {showResetHelp && (
            <div className="sa-login__note" role="status">
              <i className="fas fa-circle-info" aria-hidden></i>
              <span>
                Super Admin passwords cannot be reset from this screen. Contact
                the platform owner to have new credentials issued.
              </span>
            </div>
          )}

          <button type="submit" className="sa-login__submit" disabled={loading}>
            {loading ? (
              <>
                <span className="sa-login__spinner" aria-hidden></span>
                Signing in&hellip;
              </>
            ) : (
              <>
                Sign in
                <i className="fas fa-arrow-right" aria-hidden></i>
              </>
            )}
          </button>
        </form>

        <p className="sa-login__card-foot">
          <i className="fas fa-shield-halved" aria-hidden></i>
          Secured with an encrypted session
        </p>
      </div>
    </div>
  );
}

export default Login;
